import { useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Typography } from "@platform/design-system";

import useToastNotificationService from "../../../../../hooks/useToastNotificationService";
import RichTextEditor from "../shared/RichTextEditor/RichTextEditor";

import CarrierNotesFooter from "./CarrierNotesFooter";
import CarrierNotesTabBar from "./CarrierNotesTabBar";
import UnsavedChangesDialog from "./UnsavedChangesDialog";
import { DRAWER_EXPANDED_HEIGHT, MAX_CHARACTERS } from "./constants";
import { useAutosave } from "./hooks/useAutosave";
import {
  useCarrierNote,
  useCarrierNoteExistence,
  useUpsertCarrierNote,
} from "./hooks/useCarrierNoteApi";
import { useCarrierNotesState } from "./hooks/useCarrierNotesState";

import type { CarrierTab } from "./types";

interface CarrierNotesDrawerProps {
  carriers: CarrierTab[];
  ecoRfpId: string;
}

/**
 * Bottom-anchored drawer for per-carrier rich text notes.
 *
 * Collapsed: shows a tab bar with carrier names and status indicators.
 * Expanded: shows the TipTap editor with toolbar, editor area, and footer.
 */
const CarrierNotesDrawer = ({
  carriers,
  ecoRfpId,
}: CarrierNotesDrawerProps): React.JSX.Element | null => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeCarrierId, setActiveCarrierId] = useState(
    carriers[0]?.carrierId ?? "",
  );
  const [characterCount, setCharacterCount] = useState(0);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const isSavingForClose = useRef(false);

  const { createToastNotification } = useToastNotificationService();
  const notesState = useCarrierNotesState();
  const { seedHasContent } = notesState;
  const { data: noteExistence } = useCarrierNoteExistence({ ecoRfpId });

  // Seed which carriers have a saved note before any tab has been opened
  useEffect(() => {
    if (noteExistence?.carrierIds) {
      seedHasContent(noteExistence.carrierIds);
    }
  }, [noteExistence, seedHasContent]);

  // Set initial active carrier when carriers load
  useEffect(() => {
    if (carriers.length > 0 && !activeCarrierId) {
      setActiveCarrierId(carriers[0].carrierId);
    }
  }, [carriers, activeCarrierId]);

  const activeCarrier = carriers.find((c) => c.carrierId === activeCarrierId);
  const activeState = notesState.getState(activeCarrierId);

  // In-flight save tracker per carrier to prevent overlapping saves
  const savingCarriers = useRef(new Set<string>());

  const handleSaveSuccess = useCallback(
    (carrierId: string) => {
      savingCarriers.current.delete(carrierId);
      notesState.markSaved(carrierId);

      if (isSavingForClose.current) {
        isSavingForClose.current = false;
        setIsExpanded(false);
        setShowUnsavedDialog(false);
      }
    },
    [notesState],
  );

  const handleSaveError = useCallback(
    (carrierId: string) => {
      savingCarriers.current.delete(carrierId);
      notesState.markError(carrierId);
      isSavingForClose.current = false;
      const carrier = carriers.find((c) => c.carrierId === carrierId);
      createToastNotification({
        severity: "error",
        title: "Saving failed",
        message: `Carrier note "${carrier?.carrierName ?? "Unknown"}" wasn't saved. Please try again.`,
      });
    },
    [carriers, notesState, createToastNotification],
  );

  const { mutate: saveNote } = useUpsertCarrierNote({
    ecoRfpId,
    onSuccess: handleSaveSuccess,
    onError: handleSaveError,
  });

  const handleSave = useCallback(
    (carrierId: string, content: string) => {
      if (savingCarriers.current.has(carrierId)) return;
      savingCarriers.current.add(carrierId);
      notesState.setSaveState(carrierId, "saving");
      saveNote({ carrierId, content });
    },
    [notesState, saveNote],
  );

  const { flushSave } = useAutosave({
    content: activeState.noteContent,
    carrierId: activeCarrierId,
    isUnsaved: activeState.saveState === "unsaved",
    isWithinLimit: characterCount <= MAX_CHARACTERS,
    onSave: handleSave,
  });

  const handleContentUpdate = useCallback(
    (html: string) => {
      notesState.setContent(activeCarrierId, html);
    },
    [activeCarrierId, notesState],
  );

  const handleCharacterCount = useCallback((count: number) => {
    setCharacterCount(count);
  }, []);

  const handleTabChange = useCallback(
    (carrierId: string) => {
      if (carrierId === activeCarrierId) return;

      // Flush any pending save for the current carrier
      if (activeState.saveState === "unsaved") {
        flushSave();
      }

      setActiveCarrierId(carrierId);
      if (!isExpanded) {
        setIsExpanded(true);
      }
    },
    [activeCarrierId, activeState.saveState, flushSave, isExpanded],
  );

  const handleRetry = useCallback(
    (carrierId: string) => {
      const state = notesState.getState(carrierId);
      handleSave(carrierId, state.noteContent);
    },
    [notesState, handleSave],
  );

  const handleToggle = useCallback(() => {
    if (isExpanded) {
      if (notesState.hasUnsavedChanges()) {
        setShowUnsavedDialog(true);
        return;
      }
      setIsExpanded(false);
    } else {
      setIsExpanded(true);
    }
  }, [isExpanded, notesState]);

  const handleDialogDiscard = useCallback(() => {
    setShowUnsavedDialog(false);
    setIsExpanded(false);
  }, []);

  const handleDialogCancel = useCallback(() => {
    setShowUnsavedDialog(false);
  }, []);

  const handleDialogSave = useCallback(() => {
    isSavingForClose.current = true;
    flushSave();
  }, [flushSave]);

  if (carriers.length === 0) return null;

  return (
    <>
      <Box
        data-testid="carrier-notes-drawer"
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          width: "100%",
          zIndex: 1100,
          bgcolor: "background.paper",
          boxShadow: "0 -2px 8px rgba(0,0,0,0.12)",
          transition: "height 0.3s ease",
          height: isExpanded ? DRAWER_EXPANDED_HEIGHT : "auto",
          minHeight: 48,
          display: "flex",
          flexDirection: "column",
          overflow: isExpanded ? "hidden" : "visible",
        }}
      >
        {/* Collapsed header bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            minHeight: 48,
            borderBottom: isExpanded ? 1 : 0,
            borderColor: "divider",
            cursor: "pointer",
          }}
          onClick={handleToggle}
        >
          <Typography
            variant="body-medium"
            sx={{ mr: 2, whiteSpace: "nowrap" }}
            color="textSecondary"
          >
            <strong>Carrier Notes</strong>
          </Typography>

          <Box
            sx={{ flex: 1, overflow: "hidden" }}
            onClick={(e) => e.stopPropagation()}
          >
            <CarrierNotesTabBar
              carriers={carriers}
              activeCarrierId={activeCarrierId}
              getCarrierState={notesState.getState}
              onTabChange={handleTabChange}
              onRetry={handleRetry}
            />
          </Box>

          <IconButton size="small" aria-label="Toggle carrier notes drawer">
            {isExpanded ? (
              <KeyboardArrowDown fontSize="small" />
            ) : (
              <KeyboardArrowUp fontSize="small" />
            )}
          </IconButton>
        </Box>

        {/* Expanded content */}
        {isExpanded && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <CarrierNotesEditorWithApi
              ecoRfpId={ecoRfpId}
              carrierId={activeCarrierId}
              carrierName={activeCarrier?.carrierName ?? ""}
              notesState={notesState}
              characterCount={characterCount}
              onContentUpdate={handleContentUpdate}
              onCharacterCount={handleCharacterCount}
            />
          </Box>
        )}
      </Box>

      <UnsavedChangesDialog
        open={showUnsavedDialog}
        onDiscard={handleDialogDiscard}
        onCancel={handleDialogCancel}
        onSave={handleDialogSave}
        isSaving={isSavingForClose.current}
      />
    </>
  );
};

/**
 * Inner component that wires up the React Query API hooks
 * for the currently active carrier.
 *
 * Separated to keep hook call order stable (hooks cannot be
 * conditionally called inside the parent).
 */
function CarrierNotesEditorWithApi({
  ecoRfpId,
  carrierId,
  carrierName,
  notesState,
  characterCount,
  onContentUpdate,
  onCharacterCount,
}: {
  ecoRfpId: string;
  carrierId: string;
  carrierName: string;
  notesState: ReturnType<typeof useCarrierNotesState>;
  characterCount: number;
  onContentUpdate: (html: string) => void;
  onCharacterCount: (count: number) => void;
}): React.JSX.Element {
  // Fetch existing note for the active carrier
  const { data: savedNote } = useCarrierNote({
    ecoRfpId,
    carrierId,
  });

  // Initialize content from server when data arrives
  useEffect(() => {
    if (savedNote?.content !== undefined) {
      notesState.initializeContent(carrierId, savedNote.content);
    }
  }, [savedNote, carrierId, notesState]);

  const activeState = notesState.getState(carrierId);

  return (
    <>
      <RichTextEditor
        content={activeState.noteContent}
        placeholder={`Enter Notes for ${carrierName} ...`}
        onUpdate={onContentUpdate}
        onCharacterCount={onCharacterCount}
      />
      <CarrierNotesFooter
        characterCount={characterCount}
        saveState={activeState.saveState}
        lastSavedTimestamp={activeState.lastSavedTimestamp}
      />
    </>
  );
}

export default CarrierNotesDrawer;
